// ==============================|| BRAND LOGO SIZING ||============================== //
//
// One home for how big a tenant's logo is allowed to render in an SPA shell.
//
// ⚠ THE HEIGHT IS THE PUBLIC SITE'S EXPRESSION, COPIED ON PURPOSE. The marketing/auth site sizes its
// top-left mark with `block-size: clamp(32px, 5vw, 55px)` (`.te .te-wordmark-img`, apps/login's
// site/styles/chrome.css). The SPAs used a flat 32px, so on any desktop the same tenant's logo was 55px
// on the public page and 32px one click later inside the app — a 1.7x jump across a sign-in. Sharing the
// EXPRESSION rather than a number is what makes the two agree at every viewport width, not just at one.
//
// ⚠ CSS AND TS CANNOT SHARE THE LITERAL, so chrome.css carries a pointer back here. If you change one,
// change both; that pairing is the only thing keeping them together.
//
// ⚠ THE HEIGHT IS A CONSTRAINT, NOT A HINT — and that is why it lives in `sx` and not in an `img`
// height attribute. Tenant logos arrive as SVGs sized in MILLIMETRES (294mm x 145mm = an intrinsic
// 1112x547 CSS px): unbounded, that renders ~112px tall and the AppBar grows with it. See each app's
// berry/layout/MainLayout/shellOffsets.ts for what that cost the first time.
//
// ⚠ 56px IS THE CEILING, AND IT IS ARITHMETIC RATHER THAN TASTE. The AppBar is as tall as its tallest
// child plus the Toolbar's 2x16px padding, and `shellOffsets.ts` reserves 88px whenever the measurement
// has not landed yet (jsdom, no ResizeObserver, first paint). 56 + 32 = 88 is therefore the last height
// that cannot overshoot its own fallback. Anything above it puts the header back on top of the
// breadcrumbs in exactly the window the measurement exists to cover.
//
// ⚠ MEASURED, NOT REASONED (2026-09-14, admin, real theme + real SearchSection). At 55px the LOGO is the
// tallest thing in the row — the search input comes out at 51.1px, not the 56 the shellOffsets note
// rounds to — so the bar does grow: 83.1px -> 87px at 1440px wide. Under the 88px reserve, and the shell
// measures it anyway, so nothing is covered. Below about 1063px the clamp drops under 51.1px and the bar
// is unchanged at 83.1px; below `md` the header hides the logo entirely and stays 66px.
//
// ⚠ SO DO NOT READ "IT DOES NOT MOVE THE SHELL" INTO THIS. It moves it by 4px on a wide screen, on
// purpose. What it must never do is exceed 56.
/** `clamp(32px, 5vw, 55px)` — the public site's own logo height, so the two headers agree. */
export const BRAND_LOGO_HEIGHT = 'clamp(32px, 5vw, 55px)';

// ⚠ THE WIDTH CEILING IS DELIBERATELY *NOT* THE PUBLIC SITE'S. There it is `min(380px, 100%)`, because a
// full-width sticky bar has that room. An SPA does not: the header slot is a 228px box shared with the
// 34px menu toggle, and the drawer is 260px less its `p: 2` padding — about 180px either way. Copying 380
// here would put a wide logo through the toggle and out of the drawer.
//
// The consequence is worth knowing rather than fixing: a banner-shaped mark (wider than roughly 3.3:1,
// where 180 binds before 55 does) renders SMALLER in the app than on the public page. Matching that too
// would mean widening the header box, which moves the search field.
//
// ⚠ `100%` IS THE HALF THAT MAKES A SQUEEZED SLOT SHRINK THE IMAGE instead of overflowing it — the same
// lesson `.te-wordmark-img` records. With `objectFit: contain` beside it, pressure on the slot gives a
// smaller logo rather than a broken row.
/** `min(180px, 100%)` — the SPA header/drawer logo slot, and never wider than the box it is in. */
export const BRAND_LOGO_MAX_WIDTH = 'min(180px, 100%)';
